import {centerOfMass} from '@turf/turf';
import * as I from 'immutable';
import React from 'react';

import {HydratedFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import colors from 'app/utils/colorUtils';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import {useContinuity} from 'app/utils/hookUtils';
import * as mapUtils from 'app/utils/mapUtils';

import MapContent, {useStyleLoaded} from './MapContent';

interface Props {
  map: mapboxgl.Map;
  isMapLoaded: boolean;
  featureCollection: HydratedFeatureCollection;
  selectedFeatureIds: I.Set<number>;
  selectFeatureById: (id: number) => void;
  hoveredFeatureIds: I.Set<number>;
  hoverFeatureById: (arg: number | null) => void;
  labelOnHover?: boolean;
  customColor?: string;
  showPropertyBoundaries?: boolean;
  // send this fc to the top of the stack of all the layers
  // on the map
  sendToTop?: boolean;
  /** Portfolio: view whole collection, fill/label for selected and hovered. Focus: single-feature focus (e.g. imagery), outline-only for selected, fill/label on hover only. */
  mode: 'portfolio' | 'focus';
  /**
   * Multiplier applied to every fill-opacity value (selected/hovered/baseline).
   * Defaults to 1. Lower it (e.g. 0.5) in views where features should read as
   * muted backdrops rather than the primary visual.
   */
  fillOpacityScale?: number;
  /** Feature IDs hovered from an external panel (e.g. multi-location list). Rendered brighter than a regular mouse hover. */
  externalHoveredFeatureIds?: I.Set<number>;
}

/** Feature state key. True if the mouse is hovering over it. */
const HOVERED_KEY = 'hovered';
/** Feature state key. True if the feature is being highlighted from an external panel (e.g. multi-location list). */
const PANEL_HOVERED_KEY = 'panelHovered';

/**
 * Draws Lens features on a MapboxGL map.
 *
 * Two modes
 * portfolio - applies when multiple features are selected or the mode prop is 'portfolio'
 * focus - applies when a single feature is selected and the mode prop is 'focus'

 * portfolio mode:
 * - All properties are filled and clickable
 * - Shows property name on hover
 * - Different colors for selected properties
 *
 * focus mode:
 * - Selected property is outlined in white, no fill or hover effects
 * - Designed for viewing a single property at a time
 * - All other properties will basically get the portfolio unselected styling
 *
 * Reading mapbox style syntax:
 * ex: ['case', ['boolean', ['feature-state', HOVERED_KEY], false], 1, 0]
 * equivalent to: feature-state.HOVERED_KEY ? 1 : 0
 * Docs here: https://docs.mapbox.com/mapbox-gl-js/guides/styles/style-layers/
 *
 */
const FeatureCollectionVector: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  map,
  isMapLoaded,
  featureCollection,
  selectedFeatureIds = I.Set(),
  selectFeatureById,
  hoveredFeatureIds,
  hoverFeatureById,
  customColor,
  labelOnHover = false,
  showPropertyBoundaries = true,
  sendToTop = false,
  mode: initialMode,
  fillOpacityScale = 1,
  externalHoveredFeatureIds = I.Set(),
}) => {
  // Stabilize featureCollection by content so we don't recompute polygon/label sources when
  // the parent passes a new reference but the same features
  // (e.g. after table selectiion from the portfolio view or from realtime updates).
  featureCollection = useContinuity(featureCollection, I.is);
  const styleLoaded = useStyleLoaded(map);

  // If we have multiple selected properties, regardless of mode passed in,
  // we want to treat it as portfolio mode. This happens when a multi-location property
  // is selected before a user clicks on a specific location.
  const mode = React.useMemo(
    () => (initialMode === 'portfolio' || selectedFeatureIds.size > 1 ? 'portfolio' : 'focus'),
    [initialMode, selectedFeatureIds.size]
  );

  const polygonSource = React.useMemo(
    () => mapUtils.getSourceForFeatureCollection(featureCollection),
    [featureCollection]
  );

  // Per-feature membership test against the current selection. Selection is
  // baked into the layers' paint expressions rather than written as Mapbox
  // feature-state: feature-state writes are silently dropped while a map's
  // style or source is mid-load (e.g. compare-mode mount/unmount), which left
  // the viewed property unbolded or a previously viewed property stuck bold.
  const isSelectedExpression = React.useMemo<mapboxgl.ExpressionSpecification>(
    () =>
      ['in', ['id'], ['literal', selectedFeatureIds.toArray()]] as mapboxgl.ExpressionSpecification,
    [selectedFeatureIds]
  );

  // GeoJSON source for property names on hover. We create our own source to have more
  // control than when MapboxGL places its own labels as part of a marker layer.
  // Only recomputes when featureCollection content changes
  // (useContinuity above keeps the same reference when content is equal).
  const labelSource = React.useMemo<{
    id: string;
    source: mapboxgl.GeoJSONSourceSpecification;
  }>(() => {
    const features = featureCollection.get('features');
    const data = geoJsonUtils.featureCollection(
      features
        .map((f) => ({
          id: f!.get('id'),
          ...centerOfMass(f!.get('geometry')?.toJS(), {
            properties: {
              name: f!.getIn(['properties', 'name']),
              partName: f!.getIn(['properties', 'multiFeaturePartName']),
            },
          }),
        }))
        .toArray()
    );
    return {
      id: `${polygonSource.id}-labels`,
      source: {type: 'geojson' as const, data},
    };
  }, [polygonSource.id, featureCollection]);

  // This is designed to have styleLoaded as its dep, so that if the style goes
  // from unloaded -> loaded it re-runs any feature state–related useEffects.
  // (Since the style changing resets all feature states.)
  const setStateForFeatureId = React.useCallback(
    (id: number | string | undefined | null, state: any) => {
      if (id !== undefined && id !== null && styleLoaded?.loaded && map.isStyleLoaded()) {
        mapUtils.safeSetFeatureState(
          map,
          {source: polygonSource.id, sourceLayer: polygonSource.sourceLayer, id},
          state
        );
      }
    },
    [map, polygonSource.id, polygonSource.sourceLayer, styleLoaded]
  );

  const setLabelStateForFeatureId = React.useCallback(
    (id: number | string | undefined | null, state: any) => {
      if (id !== undefined && id !== null && styleLoaded?.loaded && map.isStyleLoaded()) {
        mapUtils.safeSetFeatureState(map, {source: labelSource.id, id}, state);
      }
    },
    [map, labelSource.id, styleLoaded]
  );

  const areClickableFeaturesDisabled = mapUtils.useAreClickableFeaturesDisabled();

  // Builds a color expression that switches between selectedColor and
  // unselectedColor based on selection in portfolio mode, or falls back to
  // unselectedColor in focus mode. Respects customColor override.
  const getColorForSelectionState = React.useCallback(
    (selectedColor: string, unselectedColor: string): mapboxgl.ExpressionSpecification | string =>
      customColor ||
      (mode === 'portfolio'
        ? ([
            'case',
            isSelectedExpression,
            selectedColor,
            unselectedColor,
          ] as mapboxgl.ExpressionSpecification)
        : unselectedColor),
    [customColor, mode, isSelectedExpression]
  );

  // Fills in features when selected or when hovered.
  // Focus mode never shows fill on the selected feature.
  const fillLayer: mapboxgl.FillLayer = React.useMemo(() => {
    return {
      type: 'fill' as const,

      id: `${polygonSource.id}-fill`,
      source: polygonSource.id,
      ...(polygonSource.sourceLayer ? {'source-layer': polygonSource.sourceLayer} : {}),

      layout: {
        // Keeps us from showing hover effects when clicking will have no
        // effect.
        visibility: areClickableFeaturesDisabled ? 'none' : 'visible',
      },

      paint: {
        'fill-color': getColorForSelectionState(colors.darkOrange, colors.primary),
        'fill-opacity':
          mode === 'focus'
            ? [
                'case',
                ['boolean', ['feature-state', PANEL_HOVERED_KEY], false],
                0.8 * fillOpacityScale,
                [
                  'all',
                  ['boolean', ['feature-state', HOVERED_KEY], false],
                  ['!', isSelectedExpression],
                ],
                0.6 * fillOpacityScale,
                0,
              ]
            : [
                'case',
                ['boolean', ['feature-state', PANEL_HOVERED_KEY], false],
                1.0 * fillOpacityScale,
                ['all', isSelectedExpression, ['boolean', ['feature-state', HOVERED_KEY], false]],
                0.9 * fillOpacityScale,
                ['any', isSelectedExpression, ['boolean', ['feature-state', HOVERED_KEY], false]],
                0.7 * fillOpacityScale,
                0.4 * fillOpacityScale,
              ],
      },
    };
  }, [
    polygonSource.id,
    polygonSource.sourceLayer,
    getColorForSelectionState,
    mode,
    areClickableFeaturesDisabled,
    fillOpacityScale,
    isSelectedExpression,
  ]);

  // Draws feature boundaries.
  // Portfolio mode: uses different colors for selected and unselected properties
  // Focus mode: uses opacity to distinguish between selected and unselected properties
  const lineLayer: mapboxgl.LineLayer = React.useMemo(
    () => ({
      type: 'line',

      id: `${polygonSource.id}-lines`,
      source: polygonSource.id,
      ...(polygonSource.sourceLayer ? {'source-layer': polygonSource.sourceLayer} : {}),

      paint: {
        'line-color': getColorForSelectionState(colors.darkOrange, colors.white),

        // For focus mode we don't want to use color to distinguish, just opacity,
        // that way we don't have to worry about the z-order of overlapping properties.
        // (We don't want a non-selected property to be drawn solidly over a selected property.)
        'line-opacity': [
          'case',
          [
            'any',
            mode === 'portfolio',
            isSelectedExpression,
            ['boolean', ['feature-state', HOVERED_KEY], false],
          ],
          1,
          0.5,
        ],
        'line-width': ['case', isSelectedExpression, 3, 2],
      },
    }),
    [
      polygonSource.id,
      polygonSource.sourceLayer,
      getColorForSelectionState,
      mode,
      isSelectedExpression,
    ]
  );

  // Black shadow underneath the property border to help it stand out
  const lineShadowLayer: mapboxgl.LineLayer = React.useMemo(
    () => ({
      type: 'line',

      id: `${polygonSource.id}-line-shadow`,
      source: polygonSource.id,
      ...(polygonSource.sourceLayer ? {'source-layer': polygonSource.sourceLayer} : {}),

      paint: {
        'line-color': '#000000',
        'line-width': mode === 'focus' ? 5 : 4,
        'line-blur': mode === 'focus' ? 0 : 1,
        'line-opacity': ['case', isSelectedExpression, 1, 0.2],
      },
    }),
    [polygonSource.id, polygonSource.sourceLayer, mode, isSelectedExpression]
  );

  // Labels for the features that appear on hover
  // usage of mapbox "format" inspired by: https://docs.mapbox.com/mapbox-gl-js/example/display-and-style-rich-text-labels/
  const labelLayer: mapboxgl.SymbolLayer = React.useMemo(
    () => ({
      type: 'symbol',

      id: `${labelSource.id}-text`,
      source: labelSource.id,

      layout: {
        'text-field': [
          'format',
          ['get', 'name'],
          {},
          '\n',
          {},
          ['get', 'partName'],
          {'font-scale': 0.8},
        ],
        'text-allow-overlap': true,
        visibility: areClickableFeaturesDisabled ? 'none' : 'visible',
      },

      paint: {
        'text-color': '#fff',
        'text-halo-color': 'rgba(0, 0, 0, 0.75)',
        'text-halo-width': 1,
        // Portfolio: show text on hover for any property
        // Focus: show text on hover for any property other than the selected property
        'text-opacity':
          mode === 'portfolio'
            ? ['case', ['boolean', ['feature-state', HOVERED_KEY], false], 1, 0]
            : [
                'case',
                [
                  'all',
                  ['boolean', ['feature-state', HOVERED_KEY], false],
                  ['!', isSelectedExpression],
                ],
                1,
                0,
              ],
      },
    }),
    [labelSource.id, mode, areClickableFeaturesDisabled, isSelectedExpression]
  );

  // Circle dots at each property centroid, visible at low zoom levels where
  // small property polygons are hard to see. Fades out as you zoom in and the
  // polygon boundaries become visible.
  //
  // Note: selected circles can overlap behind unselected ones. Mapbox's
  // circle-sort-key only supports data properties, not feature-state, so
  // fixing draw order would require splitting into two layers and embedding
  // selection into the GeoJSON source — not worth the overhead at the moment.
  const circleLayer: mapboxgl.CircleLayer = React.useMemo(
    () => ({
      type: 'circle' as const,

      id: `${labelSource.id}-circles`,
      source: labelSource.id,

      paint: {
        'circle-color': getColorForSelectionState(colors.darkOrange, colors.primary),
        // Fully visible up to zoom 10 (where small properties are hard to see),
        // then fade out by zoom 13 (where polygon boundaries are clearly visible).
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 5, 10, 5, 13, 0],
        'circle-opacity': ['interpolate', ['linear'], ['zoom'], 0, 0.9, 10, 0.9, 13, 0],
        'circle-stroke-width': 1,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-opacity': ['interpolate', ['linear'], ['zoom'], 0, 1, 10, 1, 13, 0],
      },
    }),
    [labelSource.id, getColorForSelectionState]
  );

  const activeFeatureId =
    mode === 'focus' && selectedFeatureIds.size === 1 ? selectedFeatureIds.first() : null;

  // Returns false if the event includes the currently selected feature, so it
  // can be ignored from clicks and hovers. In portfolio mode we never filter so
  // the selected property stays clickable and shows label on hover.
  const filterNoActiveFeature = React.useCallback(
    (ev: mapboxgl.MapLayerMouseEvent) => {
      if (mode === 'portfolio') return true;
      if (activeFeatureId !== null) {
        if ((ev.features || []).find((f) => f.id === activeFeatureId)) {
          return false;
        }
      }

      return true;
    },
    [activeFeatureId, mode]
  );

  mapUtils.useClickableFeatures(
    map,
    [fillLayer, lineLayer, circleLayer],
    // We can cast to number because we know our features have numeric id
    // properties. Promise.resolve() is there to get us into the next event loop
    // before changing the feature ID, since modifying the map features and
    // layers from within an event handler makes mapboxgl sad ("this.paint is
    // undefined" sad)
    (f) =>
      Promise.resolve().then(() => {
        setStateForFeatureId(f.id as number, {[HOVERED_KEY]: false});
        setLabelStateForFeatureId(f.id as number, {[HOVERED_KEY]: false});
        selectFeatureById(f.id as number);
      }),
    filterNoActiveFeature
  );

  // Updates the map’s feature state to reflect the hover values we get from
  // props.
  React.useEffect(() => {
    hoveredFeatureIds.forEach((id) => {
      setStateForFeatureId(id, {[HOVERED_KEY]: true});
      if (labelOnHover) {
        setLabelStateForFeatureId(id, {[HOVERED_KEY]: true});
      }
    });

    return () => {
      hoveredFeatureIds.forEach((id) => {
        setStateForFeatureId(id, {[HOVERED_KEY]: false});
        if (labelOnHover) {
          setLabelStateForFeatureId(id, {[HOVERED_KEY]: false});
        }
      });
    };
  }, [setStateForFeatureId, setLabelStateForFeatureId, hoveredFeatureIds, labelOnHover]);

  React.useEffect(() => {
    externalHoveredFeatureIds.forEach((id) => {
      setStateForFeatureId(id, {[PANEL_HOVERED_KEY]: true});
    });

    return () => {
      externalHoveredFeatureIds.forEach((id) => {
        setStateForFeatureId(id, {[PANEL_HOVERED_KEY]: false});
      });
    };
  }, [setStateForFeatureId, externalHoveredFeatureIds]);

  // Add handlers for hover effects on the features.
  React.useEffect(() => {
    // We track label hovering separate from feature hovering. This is so that
    // we only show one label at a time, since they’re liable to overlap if we
    // showed all labels from all parts of a multi-feature property.
    //
    // TODO(fiona): Maybe show the feature name using a different mechanism,
    // like a tool tip that follows the mouse?
    let labelHoveredFeatureId: number | null = null;

    const onMouseMove = (event: mapboxgl.MapLayerMouseEvent) => {
      // The filter function has the same semantics of Array#filter: if it
      // returns false, ignore that element.
      if (!filterNoActiveFeature(event)) {
        //If we're move our mouse to our active feature, clear the previous hover
        //and label. Otherwise we can get into a state where the hover gets stuck
        //while we're trying to look at our main feature.
        onMouseLeave();
        return;
      }

      const id = event.features?.[0].id ?? null;

      hoverFeatureById(id as number | null);

      if (id !== labelHoveredFeatureId) {
        setLabelStateForFeatureId(labelHoveredFeatureId, {[HOVERED_KEY]: false});
        setLabelStateForFeatureId(id, {[HOVERED_KEY]: true});
        labelHoveredFeatureId = id as number | null;
      }
    };

    const onMouseLeave = () => {
      // We don't have a feature ID here so we just have to unhover everything.
      // We assume that mousemove will correct this.
      hoverFeatureById(null);

      setLabelStateForFeatureId(labelHoveredFeatureId, {[HOVERED_KEY]: false});
      labelHoveredFeatureId = null;
    };

    // We need these on both the fill and the line so that features can be
    // clickable on their outline even when their fill is completely overlapped
    // by the current feature. Unfortunately this currently causes jittering as
    // the "mouseleave" event on the line layer triggers to remove the highlight
    // before the mousemove event on the fill can fix it.
    //
    // TODO(fiona): Smooth this out with a debounce or some other condition on
    // clearing the hover.
    map.on('mousemove', fillLayer.id, onMouseMove);
    map.on('mousemove', lineLayer.id, onMouseMove);
    map.on('mousemove', circleLayer.id, onMouseMove);
    map.on('mouseleave', fillLayer.id, onMouseLeave);
    map.on('mouseleave', lineLayer.id, onMouseLeave);
    map.on('mouseleave', circleLayer.id, onMouseLeave);

    return () => {
      map.off('mousemove', fillLayer.id, onMouseMove);
      map.off('mousemove', lineLayer.id, onMouseMove);
      map.off('mousemove', circleLayer.id, onMouseMove);
      map.off('mouseleave', fillLayer.id, onMouseLeave);
      map.off('mouseleave', lineLayer.id, onMouseLeave);
      map.off('mouseleave', circleLayer.id, onMouseLeave);

      setLabelStateForFeatureId(labelHoveredFeatureId, {[HOVERED_KEY]: false});
    };
  }, [
    map,
    featureCollection,
    lineLayer.id,
    fillLayer.id,
    circleLayer.id,
    setStateForFeatureId,
    setLabelStateForFeatureId,
    hoverFeatureById,
    filterNoActiveFeature,
  ]);

  React.useEffect(() => {
    if (map && isMapLoaded && sendToTop) {
      map.moveLayer(fillLayer.id);
      map.moveLayer(lineLayer.id);
    }
  }, [sendToTop, fillLayer.id, lineLayer.id, map, isMapLoaded]);

  return (
    <MapContent
      map={map}
      isMapLoaded={isMapLoaded}
      sources={[polygonSource, labelSource]}
      layers={
        showPropertyBoundaries
          ? [
              ...(mode === 'portfolio' ? [circleLayer] : []),
              fillLayer,
              lineShadowLayer,
              lineLayer,
              labelLayer,
            ]
          : []
      }
    />
  );
};

export default FeatureCollectionVector;
